import * as Api from '@ViewModels';
import { UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { isSwitchStep, useEditAutomationTemplateStepsContext } from '../../../../models/Automations';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAutomationTemplateRemoveStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateRemoveStepMutation';
import { useAutomationTemplateQuery } from '../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { canEditAutomationTemplate } from '../../../components/automation/permissions';
import { validateAutomationStep } from '../../../utils/automation-validation';
import { useStepActions } from './useStepActions';

/**
 * Fetches a child automation template by ID.
 */
async function fetchChildTemplate(
	userSession: ReturnType<typeof useUserSession>,
	childTemplateId: string,
	impersonationContext?: Api.IImpersonationContext
): Promise<Api.IAutomationTemplate | null> {
	try {
		return await userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `AutomationTemplate/${encodeURIComponent(childTemplateId)}`,
				impersonationContext,
			}),
			'GET'
		);
	} catch (error) {
		console.error(`Failed to fetch child template ${childTemplateId}:`, error);
		return null;
	}
}

/**
 * Custom hook for managing automation editor steps.
 * Replaces the EditAutomationTemplateStepsViewModel with React Query + React hooks.
 *
 * @param templateId - The ID of the automation template
 * @param impersonationContext - Optional impersonation context for API calls
 * @returns Object containing steps state and management functions
 */
export function useAutomationEditorContext() {
	const { automationTemplateId, impersonationContext, unsavedStep, setUnsavedStep, parentTrigger, isAdminContext } =
		useEditAutomationTemplateStepsContext();
	const [isLoading] = useState(false);
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const templateQueryRef = useRef<UseQueryResult<Api.IAutomationTemplate, Error> | null>(null);
	const templateQuery = useAutomationTemplateQuery({
		id: automationTemplateId || '',
		enabled: !!automationTemplateId,
		impersonationContext,
	});
	templateQueryRef.current = templateQuery;

	const removeStepMutation = useAutomationTemplateRemoveStepMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const createUuid = useCallback(() => {
		return typeof crypto !== 'undefined' && crypto.randomUUID
			? crypto.randomUUID()
			: Math.random().toString(36).slice(2);
	}, []);

	/**
	 * Adds a new step to the steps array.
	 *
	 * @param step - Optional step to add. If not provided, creates an empty step.
	 * @returns Promise that resolves to the updated steps array
	 */
	const addStep = () => {
		setUnsavedStep({});
	};

	/**
	 * Removes a step from the steps array.
	 * If the step has an ID, it will also be removed from the template via API.
	 *
	 * @param step - The step to remove
	 * @returns Promise that resolves to the updated steps array
	 */
	const removeStep = async (step: Api.IAutomationStep) => {
		if (step?.id && automationTemplateId) {
			const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(step);
			if (stepType) {
				await removeStepMutation.mutateAsync({
					templateId: automationTemplateId,
					stepType,
					stepId: step.id,
					impersonationContext,
				});
			}
		} else if (!step?.id && unsavedStep) {
			setUnsavedStep?.(undefined);
		}
	};

	const automationTemplate = React.useMemo(() => templateQuery.data, [templateQuery.data]);

	const canEdit = React.useMemo(() => {
		if (!automationTemplate) return false;

		// Admin panel can edit all non-archived templates without role checks
		if (isAdminContext) {
			return automationTemplate.status !== Api.TemplateStatus.Archived;
		}

		return canEditAutomationTemplate({
			template: automationTemplate,
			userRole: userSession.user?.role,
			impersonationContext,
		});
	}, [automationTemplate, userSession.user?.role, impersonationContext, isAdminContext]);

	const automationTemplateTrigger =
		automationTemplate?.draft?.trigger || automationTemplate?.published?.trigger || parentTrigger;

	const version = React.useMemo(() => {
		if (!automationTemplate) return null;
		return automationTemplate.draft ?? automationTemplate.published;
	}, [automationTemplate]);

	// Auto-add an unsaved step when the template is loaded but has no version/steps
	React.useEffect(() => {
		if (automationTemplate && !version && !unsavedStep) {
			setUnsavedStep?.({} as Api.IAutomationStep);
		}
	}, [automationTemplate, version, unsavedStep, setUnsavedStep]);

	const steps = React.useMemo(() => {
		if (!automationTemplate) return [];

		if (!version) {
			return unsavedStep ? [unsavedStep] : [];
		}
		return unsavedStep ? [...version.steps, unsavedStep] : version.steps;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [automationTemplate, version?.steps, unsavedStep]);

	/**
	 * Checks if all steps have IDs, which indicates they can be reordered.
	 */
	const canReorderSteps = steps.every(step => !!step?.id);

	/**
	 * Gets all incomplete steps that have missing required information.
	 * Checks both main steps and nested steps within switch statements recursively.
	 * Returns a promise that resolves to the array of incomplete steps.
	 *
	 * @returns Promise that resolves to array of steps with incomplete required information
	 */
	const getIncompleteSteps = useCallback(async (): Promise<Api.IAutomationStep[]> => {
		const incompleteSteps: Api.IAutomationStep[] = [];
		const validSteps = steps.filter(step => !!step?.id);
		const visitedTemplateIds = new Set<string>();

		const checkStepRecursively = async (step: Api.IAutomationStep): Promise<void> => {
			if (!step) {
				return;
			}

			if (!isSwitchStep(step)) {
				if (!validateAutomationStep(step)) {
					incompleteSteps.push(step);
				}
				return;
			}

			const switchStep = step;
			if (!validateAutomationStep(switchStep)) {
				incompleteSteps.push(step);
			}

			for (const caseStatement of switchStep.cases || []) {
				const caseTemplateId = caseStatement.automationTemplateId;
				if (!caseTemplateId || visitedTemplateIds.has(caseTemplateId)) {
					continue;
				}

				visitedTemplateIds.add(caseTemplateId);

				const childTemplate = await fetchChildTemplate(userSession, caseTemplateId, impersonationContext);

				const childVersion = childTemplate?.draft || childTemplate?.published;
				if (!childVersion?.steps) {
					continue;
				}
			}
		};

		if (automationTemplateId) {
			visitedTemplateIds.add(automationTemplateId);
		}
		for (const step of validSteps) {
			await checkStepRecursively(step);
		}
		return incompleteSteps;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [automationTemplateId, impersonationContext, userSession, createUuid]);

	const stepActions = useStepActions({
		automationTemplateId: automationTemplate?.id || '',
		steps,
		impersonationContext,
		automationTemplateTrigger,
		createEmptyStep: () => addStep(),
	});

	// Create stable callbacks for context - no refs needed
	const executeActionForStep = useCallback(
		async (step: Api.IAutomationStep, action: string) => {
			switch (action) {
				case 'copy':
					await stepActions.copyStep(step);
					break;
				case 'delete': {
					const clearStep = () => setUnsavedStep(null);
					await stepActions.deleteStep(step, clearStep);
					break;
				}
				case 'move-up':
					stepActions.moveStepUp(step);
					break;
				case 'move-down':
					stepActions.moveStepDown(step);
					break;
				default:
					break;
			}
		},
		[stepActions, setUnsavedStep]
	);

	return {
		steps,
		isLoading: isLoading || templateQuery.isLoading,
		canEdit,
		canReorderSteps,
		addStep,
		removeStep,
		getIncompleteSteps,
		automationTemplate,
		templateDataUpdatedAt: templateQuery.dataUpdatedAt,
		isTemplateLoading: templateQuery.isLoading,
		isTemplateError: templateQuery.isError,
		templateError: templateQuery.error,
		executeActionForStep,
		automationTemplateTrigger,
		impersonationContext,
		unsavedStep,
		setUnsavedStep,
		automationTemplateId,
	};
}
